import React, {useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'next-i18next';
import classNames from 'classnames';
import _ from 'lodash';
import { AnimateInOut } from 'Components/Animate';
import Image from 'Components/Image';
import s from './GalleryModal.module.scss';

const GalleryModal = ({
    galleryId,
    images,
    currentIndex,
    setCurrentIndex,
    closeModal,
    isOpen,
}) => {
    const {t} = useTranslation();

    const previousClickHandler = () => {
        // When previos clicked when on first image, show last image
        const newIndex = currentIndex <= 0 ? images.length - 1 : currentIndex - 1;
        setCurrentIndex(newIndex);
    };

    const nextClickHandler = () => {
        // When next clicked when on last image, show first image
        const newIndex = currentIndex < images.length - 1 ? currentIndex + 1 : 0;
        setCurrentIndex(newIndex);
    };

    const classes = classNames(
        [s['GalleryModal']],
        {[s['GalleryModal--Open']]: isOpen},
    );

    return (
        <div className={classes}>
            <button
                className={s['GalleryModal__Close']}
                onClick={(e) => closeModal(e)}
                type="button"
                aria-label={t('galleryModal.close')}
            >
                <span className="sr-only">
                    {t('galleryModal.close')}
                </span>
            </button>

            {images.length > 1 && (
                <div className={s['GalleryModal__Nav']}>
                    <button
                        className={classNames(s['GalleryModal__NavButton'], s['GalleryModal__NavButton--Previous'])}
                        type="button"
                        onClick={() => previousClickHandler()}
                    >
                        <span
                            className={s['GalleryModal__NavIcon']}
                            aria-hidden="true"
                        />
                        <span className="sr-only">
                            {t('galleryModal.previous')}
                        </span>
                    </button>
                    <button
                        className={classNames(s['GalleryModal__NavButton'], s['GalleryModal__NavButton--Next'])}
                        type="button"
                        onClick={() => nextClickHandler()}
                    >
                        <span
                            className={s['GalleryModal__NavIcon']}
                            aria-hidden="true"
                        />
                        <span className="sr-only">
                            {t('galleryModal.next')}
                        </span>
                    </button>
                </div>
            )}

            <div className={s['GalleryModal__Container']}>
                {images.map((item, i) => {
                    return (
                        <Item
                            key={`${galleryId}-modal-item-${i}`}
                            index={i}
                            image={item}
                            currentIndex={currentIndex}
                            isOpen={isOpen}
                            count={images.length}
                        />
                    );
                })}
            </div>
        </div>
    );
};

GalleryModal.propTypes = {
    galleryId: PropTypes.string,
    images: PropTypes.array,
    currentIndex: PropTypes.number,
    isOpen: PropTypes.bool,
    setCurrentIndex: PropTypes.func,
    closeModal: PropTypes.func,
};

GalleryModal.defaultProps = {
    galleryId: '',
    images: [],
    currentIndex: 0,
    isOpen: false,
    setCurrentIndex: null,
    closeModal: null,
};

const Item = ({
    image,
    index,
    currentIndex,
    count,
    isOpen,
}) => {
    const {caption} = image;

    const isActive = currentIndex === index;
    const isActiveAndOpen = isOpen && isActive;

    const [isLoaded, setIsLoaded] = useState(false);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        if(isActive && !isLoaded) {
            setIsLoaded(true);
        }
    }, [isActive, isLoaded]);

    useEffect(() => {
        if(isActiveAndOpen && isLoaded) {
            if(!isVisible) {
                setTimeout(() => {
                    setIsVisible(true);
                }, 400);
            }
        } else if(isVisible) {
            setIsVisible(false);
        }
    }, [isActiveAndOpen, isLoaded, isVisible]);

    const classes = classNames(
        [s['GalleryModal__Figure']],
        {[s['GalleryModal__Figure--Visible']]: isVisible},
    );

    return (
        <AnimateInOut
            isVisible={isActiveAndOpen}
        >
            <div>{/* Otherwise display: block will be set on Figure */}
                <figure className={classes}>
                    <span className={s['GalleryModal__Counter']}>
                        {index + 1} / {count}
                    </span>

                    {isLoaded &&
                        <Image
                            {...image}
                            className={s['GalleryModal__Image']}
                        />
                    }

                    {!_.isEmpty(caption) &&
                        <figcaption className={s['GalleryModal__Caption']}>
                            {caption}
                        </figcaption>
                    }
                </figure>
            </div>
        </AnimateInOut>
    );
};

Item.propTypes = {
    image: PropTypes.object,
    index: PropTypes.number,
    currentIndex: PropTypes.number,
    count: PropTypes.number,
    isOpen: PropTypes.bool,
};

Item.defaultProps = {
    image: {},
    index: 0,
    currentIndex: 0,
    count: 0,
    isOpen: true,
};

export default GalleryModal;
